/** Alex assistant provider for the chats SPA tab. Owns the alex conversation lifecycle: fetch-or-create on mount, the
 *  ChatRoom DO websocket, its event-driven connection state, and conversation switching. Exposes the same `useAssistant` contract. */

import {
	createContext,
	useState,
	type ReactNode,
	useEffect,
	useRef,
	useCallback,
	useMemo,
	type Dispatch,
	type SetStateAction,
	type MutableRefObject,
} from 'react';
import { useLatestRef } from '@federa/components/hooks/useLatestRef';
import { createContextHook } from '@federa/components/hooks/createContextHook';
import { LOGGER_TAGS } from '@federa/const/logging';
import { scopedLogger } from '@federa/utils/deps/registry';
import { isAssistantWarm, onAssistantWarm } from '@federa/utils/assistant/assistant-warm-signal';
import { isBrowser } from '@federa/utils/browser/detection';
import { useMessagingWebsocket } from './useMessagingWebsocket.ts';
import { useAppDispatch, useUserState, useAuthState } from './state.tsx';
import { clearOpenConversation, performConversationSetup, shouldSkipSetup, warmMayOpen } from './assistant-setup.ts';
import { buildAlexTransport } from './transport.ts';
import type { ChatRoomStruct } from '@federa/types/conversations-chat-structs';
import type { ChatFileAttachment, ConversationSetupParams } from '@federa/types/ui-chat-messaging';

const log = scopedLogger(LOGGER_TAGS.ASSISTANT_CONTEXT);

/** Latching visibility signal for the assistant pane. Attach `paneRef` to the pane root; `hasOpened` flips true the
 *  first time it is on-screen (IntersectionObserver) OR the first time the host marks it WARM (intent on the Assistant
 *  button, `assistant-warm-signal`) for a viewer who already has a room to re-adopt (`warmMayOpen`), and STAYS true --
 *  connect-on-first-open, keep the session even if the pane later hides. Falls back to opening immediately when
 *  IntersectionObserver is unavailable. */
function usePaneVisibility(userId: string | null): { paneRef: MutableRefObject<HTMLElement | null>; hasOpened: boolean } {
	const paneRef = useRef<HTMLElement | null>(null);
	// Warm at mount means the intent came before this pane existed: latch at once rather than wait to be seen.
	const [hasOpened, setHasOpened] = useState(() => isBrowser() && isAssistantWarm(window) && warmMayOpen(userId));

	useEffect(() => {
		if (hasOpened) return;
		// The identity can settle after mount, so a warm already latched on the window is re-read here, not only at mount.
		if (isAssistantWarm(window) && warmMayOpen(userId)) {
			const timer = setTimeout(() => setHasOpened(true), 0);
			return () => clearTimeout(timer);
		}
		return onAssistantWarm(window, () => {
			if (warmMayOpen(userId)) setHasOpened(true);
		});
	}, [hasOpened, userId]);

	useEffect(() => {
		if (hasOpened) return;
		const el = paneRef.current;
		if (!el) return;
		if (typeof IntersectionObserver === 'undefined') {
			// No observer available: latch open asynchronously -- a direct setState in the effect body forces a synchronous cascade render (react-hooks/set-state-in-effect).
			const timer = setTimeout(() => setHasOpened(true), 0);
			return () => clearTimeout(timer);
		}
		const observer = new IntersectionObserver((entries) => {
			if (entries.some((entry) => entry.isIntersecting)) {
				setHasOpened(true);
			}
		});
		observer.observe(el);
		return () => observer.disconnect();
	}, [hasOpened]);

	return { paneRef, hasOpened };
}

function useConversationSetup(
	currentConversationRef: MutableRefObject<ChatRoomStruct | null>,
	setCurrentConversation: Dispatch<SetStateAction<ChatRoomStruct | null>>,
	hasOpened: boolean,
) {
	const [isInitializing, setIsInitializing] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const dispatch = useAppDispatch();
	const user = useUserState();
	const auth = useAuthState();
	const previousUserIdRef = useRef<string | null>(null);
	const isSettingUpRef = useRef(false);
	const retryCountRef = useRef(0);
	const hasFailedRef = useRef(false);

	const { userId } = user;
	const { isAuthInitialized } = auth;
	// Read at setup time, NOT effect dependencies. The display name and role settle a beat after the id on a fresh
	// load, and with them in the dependency list that settle re-ran the effect: the cleanup cancelled the conversations
	// read already in flight and the new run issued it again -- measured on dev as two `conversations` POSTs on every
	// panel open, the first one discarded. Neither value changes WHICH conversation the user gets; the name only labels
	// a conversation being created for the first time.
	const usernameRef = useLatestRef(user.username);
	const roleRef = useLatestRef(user.role);

	useEffect(() => {
		// Defer guest-chat fetch-or-create until the pane has genuinely been shown (viewport-visibility latch), so pre-mounting the chats MFE never eagerly creates a guest conversation. All existing guards below still apply.
		if (!hasOpened) return;

		const currentConv = currentConversationRef.current;
		const userChanged = previousUserIdRef.current !== null && previousUserIdRef.current !== userId;

		if (userChanged) {
			retryCountRef.current = 0;
			hasFailedRef.current = false;
		}

		if (
			shouldSkipSetup(
				{ isAuthInitialized },
				{
					user: { userId },
					isSettingUpRef,
					previousUserIdRef,
					currentConv,
					hasError: hasFailedRef.current,
					retryCountRef,
				},
			)
		)
			return;

		// Guard: userId is guaranteed non-null after shouldSkipSetup check
		if (!userId) return;

		isSettingUpRef.current = true;
		const cancelledRef = { current: false };
		retryCountRef.current += 1;

		const wrappedSetError: ConversationSetupParams['setError'] = (action) => {
			const newValue = typeof action === 'function' ? action(null) : action;
			if (newValue !== null) {
				hasFailedRef.current = true;
			}
			setError(action);
		};

		void (async () => {
			const username = usernameRef.current;
			// Resolve the direct-storage-gateway transport with the caller's real credential (Firebase ID token / guest session token) before setup runs.
			const transport = await buildAlexTransport(userId, username);
			if (cancelledRef.current) return;
			await performConversationSetup({
				userId,
				username,
				role: roleRef.current,
				userChanged,
				dispatch,
				setCurrentConversation,
				setIsInitializing,
				setError: wrappedSetError,
				previousUserIdRef,
				isSettingUpRef,
				cancelledRef,
				transport,
			});
		})();

		return () => {
			cancelledRef.current = true;
			isSettingUpRef.current = false;
		};
	}, [hasOpened, userId, isAuthInitialized, dispatch, currentConversationRef, setCurrentConversation, usernameRef, roleRef]);

	return { isInitializing, error };
}

function useSwitchConversation(
	currentConversation: ChatRoomStruct | null,
	setCurrentConversation: Dispatch<SetStateAction<ChatRoomStruct | null>>,
) {
	const dispatch = useAppDispatch();

	return useCallback(
		(room: ChatRoomStruct) => {
			if (currentConversation?.id === room.id) {
				log.debug('Already on conversation:', room.id);
				return;
			}

			log.debug('Switching conversation from:', currentConversation?.id, 'to:', room.id, room.conversationType);

			clearOpenConversation(dispatch);
			setCurrentConversation(room);
		},
		[currentConversation, dispatch, setCurrentConversation],
	);
}

interface AssistantContextType {
	sendMessage: (content: string, files?: ChatFileAttachment[]) => boolean;
	isConnected: () => boolean;
	currentConversation: ChatRoomStruct | null;
	switchConversation: (room: ChatRoomStruct) => void;
	isInitializing: boolean;
	error: string | null;
	/** Attach to the pane root; flipping the visibility latch that gates setup + WS. */
	paneRef: MutableRefObject<HTMLElement | null>;
}

const AssistantContext = createContext<AssistantContextType | null>(null);

export function AssistantProvider({ children }: { children: ReactNode }) {
	const [currentConversation, setCurrentConversation] = useState<ChatRoomStruct | null>(null);
	const currentConversationRef = useLatestRef(currentConversation);
	const { paneRef, hasOpened } = usePaneVisibility(useUserState().userId);

	const { isInitializing, error } = useConversationSetup(currentConversationRef, setCurrentConversation, hasOpened);
	const switchConversation = useSwitchConversation(currentConversation, setCurrentConversation);

	const { sendMessage, isConnected } = useMessagingWebsocket({
		room: currentConversation,
		enabled: hasOpened,
	});
	// `isConnected` is state-backed inside the socket hook (its identity changes on every open/close edge), so the
	// context re-renders the moment the socket flips -- no 500ms sampler between the edge and the composer.
	const contextValue = useMemo(
		() => ({
			sendMessage,
			isConnected,
			currentConversation,
			switchConversation,
			isInitializing,
			error,
			paneRef,
		}),
		[sendMessage, isConnected, currentConversation, switchConversation, isInitializing, error, paneRef],
	);

	return <AssistantContext.Provider value={contextValue}>{children}</AssistantContext.Provider>;
}

export const useAssistant = createContextHook(AssistantContext, 'useAssistant', 'AssistantProvider');
